import Immutable from "immutable";
import keyConverter from "services/key-converter";
import { VALID_CHARACTER_AMOUNT } from "constants/greetings";

export const BaseGreeting = Immutable.Record({
  paths: Immutable.List([]),
  style: "Text",
  delay: 0,
  duration: 10,
  body: Immutable.fromJS({}),
  languages: Immutable.List([]),
  responseId: null,
  id: "",
  clientId: "",
  activeLanguage: "en",
  invalidFields: Immutable.List([]),
  isDirty: false,
  isLoading: false,
  previousState: null,
  invalidPaths: Immutable.List()
});

export function getGreetingIndex(list, id) {
  return list.findIndex(listItem => listItem.id === id);
}

export function createGreeting(greeting) {
  return new BaseGreeting({
    paths: Immutable.List(greeting.paths),
    style: greeting.style,
    delay: greeting.delay,
    duration: greeting.duration,
    body: Immutable.fromJS(greeting.body),
    languages: Immutable.List([Object.keys(greeting.body)]),
    responseId: greeting.responseId,
    clientId: greeting.clientId,
    id: greeting.id,
    activeLanguage: "en",
    invalidFields: Immutable.List([]),
    isDirty: false,
    isLoading: false,
    previousState: null,
    invalidPaths: Immutable.List([])
  });
}

export function createNewGreeting() {
  return new BaseGreeting({
    paths: Immutable.List([]),
    style: "Text",
    delay: 2,
    duration: 6,
    body: Immutable.fromJS({ "messageText": { "en": "If you need help with anything, let me know!" }, "emojiLink": "https://static.ada.support/GreetingEmojis/HandWave.svg" }),
    responseId: "5b4d0c84d63b3368eed8cbfe",
    activeLanguage: "en",
    id: "CreateGreeting",
    invalidFields: Immutable.List([]),
    isDirty: false,
    isLoading: false,
    previousState: null,
    invalidPaths: Immutable.List([])
  });
}

export function validateGreeting(greeting, greetingIndex) {
  let invalidGreeting = greeting.setIn([greetingIndex, "invalidFields"], Immutable.List([]));
  let searchPath;

  if (greeting.getIn([greetingIndex, "paths"]).size === 0) {
    invalidGreeting = invalidGreeting.updateIn([greetingIndex, "invalidFields"], arr => arr.push("paths"));
  }

  greeting.getIn([greetingIndex, "paths"]).forEach((path) => {
    if (path.substr(path.length - 1) === "/") {
      searchPath = path;
    } else {
      searchPath = `${path}/`;
    }

    if (greeting.getIn([greetingIndex, "invalidPaths"]).contains(searchPath)) {
      invalidGreeting = invalidGreeting.updateIn([greetingIndex, "invalidFields"], arr => arr.push(path));
    }
  });

  if (greeting.getIn([greetingIndex, "style"]) === "Text") {
    greeting.getIn([greetingIndex, "body", "messageText"]).forEach((value, key) => {
      if (value === "") {
        invalidGreeting = invalidGreeting.updateIn([greetingIndex, "invalidFields"], arr => arr.push(key, "bodyMissing"));
      }

      if (value.trim().length > VALID_CHARACTER_AMOUNT) {
        invalidGreeting = invalidGreeting.updateIn([greetingIndex, "invalidFields"], arr => arr.push(key, "bodyLength"));
      }
    });
  } else if (greeting.getIn([greetingIndex, "body", "emojiLink"]) === "") {
    invalidGreeting = invalidGreeting.updateIn([greetingIndex, "invalidFields"], arr => arr.push("body"));
  }

  if (greeting.getIn([greetingIndex, "responseId"]) === null) {
    invalidGreeting = invalidGreeting.updateIn([greetingIndex, "invalidFields"], arr => arr.push("responseId"));
  }

  return invalidGreeting;
}

export function saveCurrentState(greetings, greetingIndex) {
  return greetings
    .setIn([greetingIndex, "previousState"], greetings.get(greetingIndex).toJS())
    .setIn([greetingIndex, "isDirty"], true);
}

const defaultState = Immutable.List();

export const greetings = (state = defaultState, action) => {
  let greetingIndex;
  let invalidPaths;
  let nextState = state;

  greetingIndex = getGreetingIndex(state, action.greetingId);

  switch (action.type) {
    case "INITIALIZE_GREETINGS":
      return Immutable.List([createNewGreeting()]);

    case "UNDO_GREETING":
      if (state.getIn([greetingIndex, "isDirty"])) {
        return state.set(greetingIndex, createGreeting(state.getIn([greetingIndex, "previousState"])));
      }

      return state;


    case "GET_GREETINGS_SUCCESS": {
      const returnedGreetings = keyConverter(action.response.data.intros);
      returnedGreetings.forEach((greeting) => {
        nextState = nextState.push(createGreeting(greeting));
      });

      return nextState;
    }

    case "CREATE_GREETING_SUCCESS": {
      const newGreeting = createGreeting(keyConverter(action.response.data.intro));

      return state
        .push(newGreeting)
        .set(0, createNewGreeting());
    }

    case "CREATE_GREETING_FAILURE":
      invalidPaths = action.response.data.paths;
      invalidPaths.forEach((invalidPath) => {
        nextState = nextState.updateIn([0, "invalidPaths"], arr => arr.push(invalidPath));
      });

      return validateGreeting(nextState, 0);

    case "UPDATE_GREETINGS_SUCCESS": {
      const greeting = keyConverter(action.response.data.intro);
      greetingIndex = getGreetingIndex(nextState, greeting.id);
      nextState = nextState.setIn([greetingIndex, "isDirty"], false).setIn([greetingIndex, "previousState"], null);

      return nextState.setIn([greetingIndex, "invalidPaths"], Immutable.List([]));
    }

    case "UPDATE_GREETINGS_FAILURE":
      invalidPaths = action.response.data.paths;
      greetingIndex = getGreetingIndex(nextState, action[0].id);
      invalidPaths.forEach((invalidPath) => {
        nextState = nextState.updateIn([greetingIndex, "invalidPaths"], arr => arr.push(invalidPath));
      });

      return validateGreeting(nextState, greetingIndex);

    case "DELETE_GREETING_SUCCESS":
      return nextState.filter(g => g.id !== action.greetingId);

    case "UPDATE_GREETING":
      // If a key was supplied
      if (!nextState.getIn([greetingIndex, "isDirty"])) {
        nextState = saveCurrentState(nextState, greetingIndex);
      }

      if (action.key !== null) {
        nextState = nextState.setIn([greetingIndex, action.parameter, action.key], action.value);
      } else {
        nextState = nextState.setIn([greetingIndex, action.parameter], action.value);
      }

      return validateGreeting(nextState, greetingIndex);

    case "UPDATE_GREETING_BODY":
      if (!nextState.getIn([greetingIndex, "isDirty"])) {
        nextState = saveCurrentState(nextState, greetingIndex);
      }

      nextState = nextState.setIn([greetingIndex, "body", "messageText", action.key], action.value);

      return validateGreeting(nextState, greetingIndex);

    case "UPDATE_GREETING_EMOJI":
      if (!nextState.getIn([greetingIndex, "isDirty"])) {
        nextState = saveCurrentState(nextState, greetingIndex);
      }

      nextState = nextState.setIn([greetingIndex, "body", "emojiLink"], action.value);

      return validateGreeting(nextState, greetingIndex);

    case "ADD_PATH":
      if (!nextState.getIn([greetingIndex, "isDirty"])) {
        nextState = saveCurrentState(nextState, greetingIndex);
      }

      nextState = nextState.setIn([greetingIndex, "paths"], nextState.getIn([greetingIndex, "paths"]).push(action.value));

      return validateGreeting(nextState, greetingIndex);

    case "DELETE_PATH":
      if (!nextState.getIn([greetingIndex, "isDirty"])) {
        nextState = saveCurrentState(nextState, greetingIndex);
      }

      nextState = nextState.deleteIn([greetingIndex, "paths", action.key]);

      return validateGreeting(nextState, greetingIndex);

    case "ADD_LANGUAGE_TO_GREETING":
      if (!state.getIn([greetingIndex, "isDirty"])) {
        nextState = saveCurrentState(state, greetingIndex);
      }

      nextState = nextState.setIn([greetingIndex, "body", "messageText", action.language], "");

      return validateGreeting(nextState, greetingIndex);

    case "DELETE_LANGUAGE_GREETING":
      if (!nextState.getIn([greetingIndex, "isDirty"])) {
        nextState = saveCurrentState(nextState, greetingIndex);
      }

      nextState = nextState.updateIn([greetingIndex, "body", "messageText"], arr => arr.filter(
        (language, key) => key !== action.key
      ));

      // If you are deleting currently selected language, set language to primary language
      if (action.key === nextState.getIn([greetingIndex, "activeLanguage"])) {
        nextState = nextState.setIn([greetingIndex, "activeLanguage"], action.primaryLanguage);
      }

      return validateGreeting(nextState, greetingIndex);

    default:
      return nextState;
  }
};
